import React, { useEffect, useState, useCallback } from 'react';
import { cn } from '../../utils/cn';
import {
  checkForUpdate,
  getInstalledVersion,
  isSlotCoreInstalled,
} from '../../services/slot-core';

type BadgeState = 'checking' | 'up-to-date' | 'update-available' | 'not-installed' | 'error';

/**
 * Small status badge in the ProjectToolbar showing slot-core status.
 * Checks for updates on mount and allows triggering install/update via click.
 */
export const SlotCoreStatusBadge: React.FC = () => {
  const [state, setState] = useState<BadgeState>('checking');
  const [tooltip, setTooltip] = useState('Checking for updates...');

  const check = useCallback(async () => {
    setState('checking');
    setTooltip('Checking for updates...');

    try {
      const installed = await isSlotCoreInstalled();
      if (!installed) {
        setState('not-installed');
        setTooltip('Slot Core not installed. Click to install.');
        return;
      }

      const result = await checkForUpdate();
      const version = await getInstalledVersion();
      const shortSha = version?.sha.slice(0, 7) || '???';

      if (result.hasUpdate) {
        setState('update-available');
        setTooltip(`Slot Core update available (${shortSha} → ${result.remoteSha?.slice(0, 7) || 'latest'})`);
      } else {
        setState('up-to-date');
        setTooltip(`Slot Core up to date (${shortSha})`);
      }
    } catch {
      setState('error');
      setTooltip('Failed to check updates. Click to retry.');
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  // Re-check when slot-core install/update completes
  useEffect(() => {
    const handleComplete = () => {
      // Small delay to let files settle
      setTimeout(check, 500);
    };
    window.addEventListener('slot-core:install-complete', handleComplete);
    return () => window.removeEventListener('slot-core:install-complete', handleComplete);
  }, [check]);

  const handleClick = () => {
    if (state === 'not-installed') {
      window.dispatchEvent(new CustomEvent('slot-core:install-requested'));
    } else if (state === 'update-available') {
      window.dispatchEvent(new CustomEvent('slot-core:update-requested'));
    } else if (state === 'error') {
      check();
    }
  };

  const isClickable = state === 'not-installed' || state === 'update-available' || state === 'error';

  return (
    <button
      onClick={handleClick}
      disabled={state === 'checking' || state === 'up-to-date'}
      className={cn(
        'flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors',
        isClickable
          ? 'bg-zinc-700 hover:bg-zinc-600 cursor-pointer'
          : 'bg-zinc-700 cursor-default',
        state === 'checking' && 'animate-pulse',
      )}
      title={tooltip}
    >
      <span className="font-medium text-zinc-400">SC</span>
      <BadgeIcon state={state} />
    </button>
  );
};

const BadgeIcon: React.FC<{ state: BadgeState }> = ({ state }) => {
  switch (state) {
    case 'checking':
      return <span className="text-zinc-400">&#9676;</span>; // ◌
    case 'up-to-date':
      return <span className="text-green-400">&#10003;</span>; // ✓
    case 'update-available':
      return <span className="text-yellow-400">&uarr;</span>; // ↑
    case 'not-installed':
      return <span className="text-zinc-500">&#10005;</span>; // ✕
    case 'error':
      return <span className="text-red-400">!</span>;
  }
};
